// A spreadsheet or csv read as a table.
//
// The workbook is parsed on the SERVER and only cell values cross the wire: shipping a parser to the
// browser to re-read a file the server already has would cost a megabyte of bundle for a worse answer,
// and the same method answers for csv so a reader never has to care which it is.

import React, { useState } from 'react';
import styled from 'styled-components';
import { strings } from '/imports/strings';
import { css } from '/imports/ui/css';
import { Modal, ModalStyled } from '/imports/ui/Modal';
import { Select } from '/imports/ui/Select';
import { useMethod } from '/imports/hooks';
import { drivePreviewSheet } from '/imports/api/drive';

const DriveSheetModalStyled = styled(ModalStyled)`
  .modal-window { width: 90vw; max-width: 100%; }
  .sheet-bar { display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.5rem; }
  .sheet-bar select { width: auto; height: 2.2rem; min-height: 2.2rem; font-size: 0.85rem; }
  .sheet-scroll { overflow: auto; max-height: 65vh; border: 1px solid ${css.grey7}; }
  table.sheet { border-collapse: collapse; font-size: 0.8rem; }
  table.sheet td, table.sheet th {
    border: 1px solid ${css.grey7}; padding: 0.2rem 0.4rem; white-space: nowrap;
    max-width: 22rem; overflow: hidden; text-overflow: ellipsis;
  }
  table.sheet th {
    background: ${css.grey9}; color: ${css.grey3}; font-weight: 600; position: sticky; top: 0;
    font-variant-numeric: tabular-nums;
  }
  table.sheet td.num { text-align: right; font-variant-numeric: tabular-nums; }
  table.sheet tbody tr:first-child td { font-weight: 600; }
`;

export interface DriveSheetModalProps {
  _id: string;
  name: string;
  onClose(): void;
}

export const DriveSheetModal = (props: DriveSheetModalProps) => {
  const [sheet, setSheet] = useState<string>();
  const [preview, loading] = useMethod(drivePreviewSheet, { _id: props._id, sheet }, [props._id, sheet]);
  const columns = Math.max(0, ...(preview?.rows || []).map((row) => row.length));

  return (
    <Modal styled={DriveSheetModalStyled} title={props.name} onClose={props.onClose}>
      {(preview?.sheets.length || 0) > 1 && (
        <div className="sheet-bar">
          <label htmlFor="sheet-pick">{strings.Sheet()}</label>
          <Select
            id="sheet-pick"
            value={preview?.sheet}
            options={(preview?.sheets || []).map((name) => ({ value: name, label: name }))}
            onChange={(value) => setSheet(value)}
          />
        </div>
      )}
      {loading && !preview && <div className="muted">{strings.Loading()}</div>}
      {preview && (
        <>
          <div className="sheet-scroll">
            <table className="sheet">
              <thead>
                <tr>
                  <th />
                  {Array.from({ length: columns }).map((_, index) => (
                    // Spreadsheet column letters, so what is on screen can be talked about by cell.
                    <th key={index}>{String.fromCharCode(65 + (index % 26)).repeat(Math.floor(index / 26) + 1)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    <th>{rowIndex + 1}</th>
                    {Array.from({ length: columns }).map((_, cellIndex) => {
                      const value = row[cellIndex];
                      return (
                        <td key={cellIndex} className={typeof value === 'number' ? 'num' : ''}>
                          {value === null || value === undefined ? '' : `${value}`}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {preview.truncated && <div className="muted">{strings.DrivePreviewTruncated(preview.rows.length)}</div>}
        </>
      )}
    </Modal>
  );
};
